import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import MobileMerchantDetailsTab from '../../components/app_components/mobile/merchant/MobileMerchantDetailsTab';
import MobileMerchantPlansTab from '../../components/app_components/mobile/merchant/MobileMerchantPlansTab';
import IOSMobileMerchantPlansTab from '../../components/app_components/mobile/merchant/IOSMobileMerchantPlansTab';
import MobileMerchantStatsTab from '../../components/app_components/mobile/merchant/MobileMerchantStatsTab';
import MobileSimilarMerchants from '../../components/app_components/mobile/merchant/MobileSimilarMerchants';
import { 
  Home, BellDot, Bell, ShoppingCart, ShoppingBag, Users, Settings, 
  CreditCard, HelpCircle, BarChart2, Star, Loader2, Check, 
  X, Globe, Mail, Phone, Calendar, Lock, Share2, ChevronRight, ChevronLeft,
  ExternalLink
} from 'lucide-react';
import { useTranslation } from "../../contexts/LanguageContext";
import { useAuth } from "../../App";

// Helper function to parse MongoDB Extended JSON
const parseExtendedJSON = (data) => {
  const parseValue = (value) => {
    if (value && typeof value === 'object') {
      if (value.$oid) return value.$oid;
      if (value.$numberInt) return parseInt(value.$numberInt, 10);
      if (value.$numberDouble) return parseFloat(value.$numberDouble);
      if (value.$numberLong) return parseInt(value.$numberLong, 10);
      if (value.$date) return new Date(value.$date.$numberLong ? parseInt(value.$date.$numberLong, 10) : value.$date);
    }
    return value;
  };

  const deepParse = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(deepParse);
    } else if (obj && typeof obj === 'object') {
      const parsed = {};
      for (const key in obj) {
        parsed[key] = deepParse(parseValue(obj[key]));
      }
      return parsed;
    }
    return obj;
  };

  return deepParse(data);
};

// Premium check utility
const checkPremiumTrackingLimit = (isPremium, currentTrackedCount, maxFreeLimit = 5) => {
  if (isPremium) {
    return {
      canTrack: true,
      maxTracked: 'unlimited',
      remaining: 'unlimited',
      isLimited: false,
      currentCount: currentTrackedCount
    };
  } else {
    return {
      canTrack: currentTrackedCount < maxFreeLimit,
      maxTracked: maxFreeLimit,
      remaining: maxFreeLimit - currentTrackedCount,
      isLimited: true,
      currentCount: currentTrackedCount,
      error: currentTrackedCount >= maxFreeLimit 
        ? `You've reached the limit of ${maxFreeLimit} tracked merchants. Upgrade to premium for unlimited tracking.`
        : null
    };
  }
};

const MobileMerchant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { merchantId } = useParams();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth();
  
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('plans');
  const [isPremium, setIsPremium] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionUpdated, setSubscriptionUpdated] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [userFreeTrialsCount, setUserFreeTrialsCount] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [promoNotificationsEnabled, setPromoNotificationsEnabled] = useState(false);
  const [trackedMerchantsCount, setTrackedMerchantsCount] = useState(0);
  const [trackingLimit, setTrackingLimit] = useState({
    canTrack: true,
    maxTracked: 5,
    remaining: 5,
    isLimited: false,
    currentCount: 0,
    error: null
  });

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate("/demo");
    }
  }, [user, navigate]);

  const fetchUnreadNotifications = async () => {
    try {
      if (!user) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchUnreadNotifications();
        }
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err.status === 401) {
        navigate("/demo");
      }
    }
  };

  const fetchUserFreeTrialsCount = async () => {
    try {
      if (!user) return;

      const response = await httpClient.request('/api/free-trial-list');
      
      if (response.ok) {
        const freeTrials = await response.json();
        setUserFreeTrialsCount(freeTrials.length);
      } else {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching free trials count:', error);
    }
  };

  const fetchPromoNotificationsStatus = async () => {
    try {
      if (!user || !merchantId) return;

      const response = await httpClient.request(
        `/api/check-promo-notifications?merchant_id=${merchantId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setPromoNotificationsEnabled(data.enabled);
      } else {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching promo notifications status:', error);
    }
  };

  const fetchTrackedMerchantsCount = async () => {
    try {
      if (!user) return 0;

      const response = await httpClient.request('/api/user/tracked-merchants/count');
      
      if (response.ok) {
        const data = await response.json();
        setTrackedMerchantsCount(data.count);
        return data.count;
      } else {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
      }
      
      return 0;
    } catch (error) {
      console.error('Error fetching tracked merchants count:', error);
      return 0;
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      if (!user) return false;

      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return premiumData.isPremium;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        const isUserPremium = fallbackData.userPlan === 'premium';
        setIsPremium(isUserPremium);
        return isUserPremium;
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Update tracking limit when premium status or tracked count changes
  useEffect(() => {
    const limit = checkPremiumTrackingLimit(isPremium, trackedMerchantsCount, 5);
    setTrackingLimit(limit);
  }, [isPremium, trackedMerchantsCount]);

  // Updated navItems with translations
  const navItems = [
    { icon: Home, label: t('bottomNav.home') || "Home", path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications') || "Notifications",
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace') || "Marketplace", path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs') || "Share Subs", path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank') || "Connect Bank", path: "/mobile-connectBank" },
    { icon: Users, label: t('bottomNav.partners') || "Partners", path: "/mobile-partners" },
    { icon: Settings, label: t('bottomNav.settings') || "Settings", path: "/mobile-account-settings" },
  ];

  const fetchActivePlan = async () => {
    try {
      if (!user || !merchantId) return;

      const response = await httpClient.request('/api/subscription-list');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        throw new Error('Failed to fetch subscriptions');
      }
      
      const subscriptions = await response.json();
      
      // Parse the subscriptions to handle MongoDB Extended JSON format
      const parsedSubscriptions = parseExtendedJSON(subscriptions);

      // Find active subscription for this merchant (either custom or non-custom)
      const activeSubscriptionForMerchant = parsedSubscriptions.find(
        sub => sub.merchant_id === merchantId
      );
      
      setActiveSubscription(activeSubscriptionForMerchant || null);
      
    } catch (error) {
      console.error('Error fetching active plan:', error);
      setActiveSubscription(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching merchant data for ID:', merchantId);

        // Fetch user data in parallel
        await Promise.all([
          fetchPremiumStatus(),
          fetchTrackedMerchantsCount()
        ]);

        const merchantResponse = await httpClient.request(
          `/api/merchant?id=${merchantId}`,
          { headers: { 'Content-Type': 'application/json' } }
        );
    
        if (!merchantResponse.ok) throw new Error(`HTTP error! status: ${merchantResponse.status}`);
        
        const merchantData = await merchantResponse.json();
        
        // Parse Extended JSON format
        const parsedMerchant = parseExtendedJSON(merchantData);
        
        const transformedMerchant = {
          id: parsedMerchant._id.toString(),
          name: parsedMerchant.name,
          description: `${parsedMerchant.name} - ${parsedMerchant.category}`,
          category: parsedMerchant.category,
          logo: parsedMerchant.logo,
          website: parsedMerchant.website,
          affiliate_link: parsedMerchant.affiliate_link || null,
          email: parsedMerchant.email || "No Data",
          phone: parsedMerchant.phone || "No Data",
          founded: parsedMerchant.founded || "No Data",
          subscriptionPlans: parsedMerchant.subscription_plans.map(plan => ({
            id: plan.plan_id,
            name: plan.plan,
            price: plan.price,
            interval: plan.billing_interval,
            features: plan.features,
            currency: plan.currency || "EUR",
            userCount: 0,
            color: "#" + Math.floor(Math.random()*16777215).toString(16)
          })),
          stats: {
            subscribers: 0,
            avgRating: 0,
            customerSupport: "24/7",
            satisfactionRate: 0
          }
        };
        
        setMerchant(transformedMerchant);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching merchant:', err);
        setError(err.message);
        setLoading(false);
      }
    };
  
    if (user && merchantId) {
      fetchData();
      fetchActivePlan();
      fetchUserFreeTrialsCount();
      fetchPromoNotificationsStatus();
    }
  }, [merchantId, subscriptionUpdated, user]);

  // UPDATED: handleAddToList function
  const handleAddToList = async (plan) => {
    setIsAdding(true);
    setShowSuccess(false);
    setShowError(false);

    try {
      if (!user) throw new Error(t('common.authenticationRequired') || "Authentication required");

      if (!merchant?.id) throw new Error("Missing merchant ID");
      if (!merchant.name) throw new Error("Missing merchant name");
      if (!merchant.category) throw new Error("Missing category");
      if (!plan?.name) throw new Error("Missing plan name");
      if (!plan.price) throw new Error("Missing price");
      
      const billingInterval = plan.billing_interval || plan.interval || 'monthly';

      console.log('Adding/replacing subscription with:', {
        merchantId: merchant.id,
        planId: plan.id,
        planName: plan.name,
        price: plan.price,
        interval: billingInterval,
        is_custom: plan.is_custom || false
      });

      if (activeSubscription) {
        console.log('Existing subscription found, deleting first:', activeSubscription);
        await handleDeleteSubscription();
      }

      const subscriptionData = {
        subscriptions: [{
          merchant_id: merchant.id,
          merchant_name: merchant.name,
          category: merchant.category,
          plan_id: plan.id,
          plan_name: plan.name,
          price: plan.price,
          currency: plan.currency || "EUR",
          billing_interval: billingInterval,
          is_custom: plan.is_custom || false,
          features: plan.features || [],
          start_date: new Date().toISOString(),
          status: "active"
        }]
      };

      console.log('Sending subscription data:', subscriptionData);

      const response = await httpClient.request("/api/save-subscriptions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      });

      const data = await response.json();
      console.log('Add subscription response:', data);

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        throw new Error(data.error || data.message || 'Failed to save subscription');
      }

      await fetchActivePlan();
      setSubscriptionUpdated(prev => prev + 1);
      
      const message = plan.is_custom 
        ? (t('merchant.messages.customSubscriptionAdded') || 'Custom plan created and replaced successfully!')
        : (t('merchant.messages.subscriptionReplaced') || 'Subscription replaced successfully!');
      
      setSuccessMessage(message);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error adding subscription:', error);
      setErrorMessage(error.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddToFreeTrial = async (plan, endDate, automaticRenewal) => {
    setIsAdding(true);
    setShowSuccess(false);
    setShowError(false);
  
    try {
      if (!user) throw new Error(t('common.authenticationRequired') || "Authentication required");
  
      if (!merchant?.id) throw new Error("Missing merchant ID");
      if (!merchant.name) throw new Error("Missing merchant name");
      if (!merchant.category) throw new Error("Missing category");
      if (!plan?.name) throw new Error("Missing plan name");
      if (!plan.price) throw new Error("Missing price");
      if (!plan.interval) throw new Error("Missing billing interval");
  
      const endOfTrial = new Date(endDate);

      const freeTrialData = {
        merchant_id: merchant.id,
        merchant_name: merchant.name,
        category: merchant.category,
        plan_id: plan.id,
        plan_name: plan.name,
        price: plan.price,
        currency: plan.currency || "EUR",
        billing_interval: plan.interval,
        end_of_trial: endOfTrial.toISOString(),
        automatic_renewal: automaticRenewal,
        status: "active"
      };

      const response = await httpClient.request("/api/add-free-trial", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          free_trials: [freeTrialData]
        }),
      });
  
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        throw new Error(data.error || 'Failed to save free trial');
      }

      setUserFreeTrialsCount(prev => prev + 1);
      
      setSuccessMessage(t('merchant.messages.freeTrialStarted') || 'Free trial started successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddToFavorites = async () => {
    try {
      if (!user) throw new Error(t('common.authenticationRequired') || "Authentication required");
      if (!merchantId) throw new Error("Missing merchant ID");

      const response = await httpClient.request("/api/toggle-favorite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          merchant_id: merchantId,
          is_favorite: !isFavorite
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        throw new Error(data.error || 'Failed to update favorite status');
      }

      setIsFavorite(!isFavorite);
      setSuccessMessage(!isFavorite ? 
        t('merchant.messages.addedToFavorites') || 'Added to favorites!' : 
        t('merchant.messages.removedFromFavorites') || 'Removed from favorites!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleTogglePromoNotifications = async () => {
    try {
      if (!user) throw new Error(t('common.authenticationRequired') || "Authentication required");
      if (!merchantId) throw new Error("Missing merchant ID");

      const newEnabledState = !promoNotificationsEnabled;
      
      if (newEnabledState && !trackingLimit.canTrack) {
        throw new Error(trackingLimit.error || 'You have reached the limit for tracking merchants');
      }

      const response = await httpClient.request("/api/toggle-promo-notifications", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          merchant_id: merchantId,
          enabled: newEnabledState
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        
        if (data.limitReached) {
          throw new Error(data.error || 'You have reached the limit for tracking merchants');
        }
        throw new Error(data.error || 'Failed to update notification settings');
      }

      setPromoNotificationsEnabled(newEnabledState);
      
      if (data.trackedCount !== undefined) {
        setTrackedMerchantsCount(data.trackedCount);
      }
      
      setSuccessMessage(newEnabledState ? 
        t('merchant.messages.promoAlertsEnabled') || 'Promo notifications enabled!' : 
        t('merchant.messages.promoAlertsDisabled') || 'Promo notifications disabled!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error toggling promo notifications:', error);
      setErrorMessage(error.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleDeleteSubscription = async () => {
    try {
      if (!user) throw new Error(t('common.authenticationRequired') || "Authentication required");

      const response = await httpClient.request(
        "/api/delete-subscription",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            merchant_id: merchantId
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
        }
        throw new Error(data.error || 'Failed to delete subscription');
      }

      setActiveSubscription(null);
      setSubscriptionUpdated(prev => prev + 1);
      
      setSuccessMessage(t('merchant.messages.subscriptionRemoved') || 'Subscription removed successfully');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  // Handler for website redirect (uses affiliate_link first)
  const handleMerchantWebsiteClick = () => {
    if (!merchant) return;
    
    const url = merchant.affiliate_link || merchant.website;
    if (!url) {
      setErrorMessage(t('merchant.common.noWebsite') || 'No website available');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }
    
    if (window.confirm(t('merchant.common.redirectConfirm') || 'You are about to leave the app and visit the merchant website. Continue?')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Detect platform
  const isIOS = Capacitor.getPlatform() === 'ios';

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />

      <button
        onClick={() => navigate("/mobile-marketplace")}
        className="flex items-center text-[#253985] p-2 -ml-2"
      >
        <ChevronLeft className="h-5 w-5 mr-1" />
        <span className="text-sm">{t('marketplaceCategory.backToMarketplace') || "Back to Marketplace"}</span>
      </button>

      {/* Status Messages */}
      {showSuccess && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg mx-4">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              <span>{successMessage}</span>
            </div>
          </div>
        </div>
      )}

      {showError && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg mx-4">
            <div className="flex items-center gap-2">
              <X className="h-5 w-5" />
              <span>{errorMessage}</span>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Merchant Header with external link icon */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg transition-all duration-500`}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold">{merchant?.name || t('merchant.common.merchantDetails') || "Merchant Details"}</h1>
                <p className="text-white/90 text-sm">{merchant?.description || t('merchant.common.loadingMerchantInformation') || "Loading..."}</p>
              </div>
              {merchant?.logo && (
                <div className="flex items-center gap-2">
                  {/* Only render the external link button if NOT on iOS */}
                  {!isIOS && (merchant.affiliate_link || merchant.website) && (
                    <button
                      onClick={handleMerchantWebsiteClick}
                      className="w-7 h-7 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                      title={t('merchant.common.visitWebsite') || 'Visit website'}
                    >
                      <ExternalLink className="h-4 w-4 text-white" />
                    </button>
                  )}
                  <img 
                    src={merchant.logo} 
                    alt="Merchant logo" 
                    className="h-14 w-14 bg-white rounded-lg p-1 object-contain"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/56'}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-[#253985]" />
                <span>{t('common.loading') || "Loading..."}</span>
              </div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium">
                {t('common.error') || "Error"} {t('merchant.common.loadingMerchant') || "loading merchant"}
              </h3>
              <p className="text-red-600 mt-1">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-sm text-red-600 hover:text-red-800"
              >
                {t('common.tryAgain') || "Try again"}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex overflow-x-auto pb-2">
                {['plans', 'details', 'stats'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-shrink-0 px-4 py-2 text-sm font-medium ${
                      activeTab === tab
                        ? 'border-b-2 border-[#253985] text-[#253985]'
                        : 'text-gray-500'
                    }`}
                  >
                    {tab === 'plans' && (t('merchant.common.plans') || "Plans")}
                    {tab === 'details' && (t('merchant.common.details') || "Details")}
                    {tab === 'stats' && (t('merchant.common.statistics') || "Statistics")}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'details' && (
                <MobileMerchantDetailsTab merchant={merchant} />
              )}

              {activeTab === 'plans' && (
                isIOS ? (
                  <IOSMobileMerchantPlansTab 
                    merchant={merchant}
                    isAdding={isAdding}
                    handleAddToList={handleAddToList}
                    handleAddToFreeTrial={handleAddToFreeTrial}
                    handleAddToFavorites={handleAddToFavorites}
                    handleTogglePromoNotifications={handleTogglePromoNotifications}
                    activeSubscription={activeSubscription}
                    onDeleteSubscription={handleDeleteSubscription}
                    userFreeTrialsCount={userFreeTrialsCount}
                    promoNotificationsEnabled={promoNotificationsEnabled}
                    isPremium={isPremium}
                    trackingLimit={trackingLimit}
                    trackedMerchantsCount={trackedMerchantsCount}
                    onUpgrade={() => navigate('/mobile-upgrade')}
                    onSubscriptionUpdated={() => {
                      fetchActivePlan();
                      setSubscriptionUpdated(prev => prev + 1);
                    }}
                  />
                ) : (
                  <MobileMerchantPlansTab 
                    merchant={merchant}
                    isAdding={isAdding}
                    handleAddToList={handleAddToList}
                    handleAddToFreeTrial={handleAddToFreeTrial}
                    handleAddToFavorites={handleAddToFavorites}
                    handleTogglePromoNotifications={handleTogglePromoNotifications}
                    activeSubscription={activeSubscription}
                    onDeleteSubscription={handleDeleteSubscription}
                    userFreeTrialsCount={userFreeTrialsCount}
                    promoNotificationsEnabled={promoNotificationsEnabled}
                    isPremium={isPremium}
                    trackingLimit={trackingLimit}
                    trackedMerchantsCount={trackedMerchantsCount}
                    onUpgrade={() => navigate('/mobile-upgrade')}
                    onSubscriptionUpdated={() => {
                      fetchActivePlan();
                      setSubscriptionUpdated(prev => prev + 1);
                    }}
                  />
                )
              )}

              {activeTab === 'stats' && (
                <MobileMerchantStatsTab 
                  merchant={merchant}
                  isPremiumUser={isPremium}
                />
              )}

              {/* Similar Merchants Section */}
              {merchant?.category && (
                <MobileSimilarMerchants 
                  currentMerchantId={merchantId}
                  category={merchant.category}
                />
              )}
            </div>
          )}
        </div>
      </main>

      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileMerchant;
